import React, { useEffect, useMemo, useState } from "react";
import { FiAlertCircle, FiCheckCircle, FiChevronLeft, FiChevronRight, FiClipboard, FiClock, FiSave, FiSend, FiX } from "react-icons/fi";
import { getAppraisalForm, getForms, saveAppraisalDraftForm, submitAppraisalForm } from "../api/hrmApi";
import ComponentLoader from "../components/ComponentLoader";
import ScreenHeader from "./ScreenHeader";
import ScreenMetric from "./ScreenMetric";

const APPRAISAL_ANSWER_PLACEHOLDER =
  "Please write naturally. We are not judging grammar. We are judging honesty, ownership, clarity, and real contribution.";

const getPayload = (payload) => payload?.data || payload || {};

const normalizeForms = (payload) => {
  const data = getPayload(payload);
  const forms = Array.isArray(data.forms) ? data.forms : Array.isArray(payload) ? payload : [];
  return forms.map((form) => ({
    id: form.id || form.formId || "appraisal",
    title: form.title || form.name || "Appraisal Form",
    type: form.type || "Appraisal Form",
    description: form.description || "",
    dueText: form.dueText || "Closed",
    status: form.status || (form.submitted ? "Submitted" : "Draft"),
    active: form.active !== false && !form.submitted,
    requiresAction: Boolean(form.requiresAction),
    submitted: Boolean(form.submitted),
    submittedAt: form.submittedAt || "",
    submissionId: form.submissionId || "",
  }));
};

const normalizeFormDetail = (payload) => {
  const form = getPayload(payload).form || {};
  return {
    id: form.id || "appraisal",
    title: form.title || "Appraisal Form",
    info: form.info || "",
    expiryNote: form.expiryNote || "This form is closed.",
    status: form.status || (form.submitted ? "Submitted" : "Draft"),
    readOnly: Boolean(form.readOnly),
    steps: Array.isArray(form.steps) ? form.steps : [],
    questions: Array.isArray(form.questions) ? form.questions : [],
    answers: form.answers && typeof form.answers === "object" && !Array.isArray(form.answers) ? form.answers : {},
  };
};

const hasText = (value) => typeof value === "string" && value.trim().length > 0;

const isAnswerFilled = (question, value) => {
  if (question.type === "rating") {
    return Boolean(value && typeof value === "object" && value.rating && hasText(value.explanation || ""));
  }
  if (question.type === "choice") {
    return Boolean(value && typeof value === "object" && value.choice && hasText(value.explanation || ""));
  }
  if (question.type === "yesNo") {
    if (!value || typeof value !== "object") {
      return false;
    }
    if (value.choice === "No") {
      return true;
    }
    return Boolean(value.choice === "Yes" && hasText(value.explanation || ""));
  }
  return hasText(value || "");
};

const getMissingRequiredCount = (questions, answers) =>
  questions.filter((question) => question.required && !isAnswerFilled(question, answers[question.id])).length;

const MyFormsScreen = ({
  forms = [],
  loading = false,
  loadingLabel = "Loading forms",
  onFormsChange,
  viewUserId = "",
}) => {
  const [formRows, setFormRows] = useState(forms);
  const [selectedFormId, setSelectedFormId] = useState("");
  const [formDetail, setFormDetail] = useState(null);
  const [answers, setAnswers] = useState({});
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [detailLoading, setDetailLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [declarationOpen, setDeclarationOpen] = useState(false);
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    setFormRows(forms);
  }, [forms]);

  const refreshForms = async () => {
    const payload = await getForms(viewUserId);
    const nextForms = normalizeForms(payload);
    setFormRows(nextForms);
    onFormsChange?.(nextForms);
    return nextForms;
  };

  const openForm = async (formId = "appraisal") => {
    setSelectedFormId(formId);
    setError("");
    setMessage("");
    setDetailLoading(true);
    try {
      const payload = await getAppraisalForm(viewUserId);
      const detail = normalizeFormDetail(payload);
      setFormDetail(detail);
      setAnswers(detail.answers || {});
      setActiveStepIndex(0);
    } catch (loadError) {
      setError(loadError.message || "The form could not be loaded.");
    } finally {
      setDetailLoading(false);
    }
  };

  const stepNames = useMemo(() => {
    if (!formDetail?.questions?.length) {
      return [];
    }
    return Array.from(new Set(formDetail.questions.map((question) => question.step || "Form")));
  }, [formDetail]);

  const activeStep = stepNames[activeStepIndex] || "";
  const activeQuestions = useMemo(
    () => (formDetail?.questions || []).filter((question) => (question.step || "Form") === activeStep),
    [activeStep, formDetail]
  );
  const activeStepRequiredCount = activeQuestions.filter((question) => question.required).length;
  const activeStepCompletedCount = activeQuestions.filter((question) => question.required && isAnswerFilled(question, answers[question.id])).length;
  const requiredCount = (formDetail?.questions || []).filter((question) => question.required).length;
  const missingRequiredCount = getMissingRequiredCount(formDetail?.questions || [], answers);
  const completedRequiredCount = Math.max(requiredCount - missingRequiredCount, 0);
  const openCount = formRows.filter((form) => form.requiresAction).length;
  const submittedCount = formRows.filter((form) => form.submitted || form.status === "Submitted").length;
  const busy = savingDraft || submitting;

  const updateAnswer = (questionId, value) => {
    setAnswers((current) => ({
      ...current,
      [questionId]: value,
    }));
  };

  const updateNestedAnswer = (questionId, key, value) => {
    setAnswers((current) => ({
      ...current,
      [questionId]: {
        ...(current[questionId] && typeof current[questionId] === "object" ? current[questionId] : {}),
        [key]: value,
      },
    }));
  };

  const saveDraft = async ({ advance = false } = {}) => {
    if (!formDetail || formDetail.readOnly) {
      return false;
    }
    setSavingDraft(true);
    setError("");
    setMessage("");
    try {
      const payload = await saveAppraisalDraftForm(viewUserId, {
        formId: formDetail?.id || selectedFormId || "appraisal",
        answers,
      });
      const savedForm = getPayload(payload).form || {};
      setFormDetail((current) =>
        current
          ? {
              ...current,
              status: savedForm.status || "Draft",
              readOnly: false,
              submissionId: savedForm.submissionId || current.submissionId,
            }
          : current
      );
      await refreshForms();
      if (advance) {
        setActiveStepIndex((current) => Math.min(current + 1, stepNames.length - 1));
      }
      setMessage(advance ? "Draft saved. Continue with the next section." : "Draft saved. You can continue later.");
      return true;
    } catch (saveError) {
      setError(saveError.message || "The appraisal draft could not be saved.");
      return false;
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmit = () => {
    if (missingRequiredCount > 0) {
      setError("Complete every required question before submitting.");
      return;
    }
    setDeclarationAccepted(false);
    setDeclarationOpen(true);
  };

  const confirmSubmit = async () => {
    if (!declarationAccepted) {
      setError("Accept the declaration before submitting.");
      return;
    }
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      await submitAppraisalForm(viewUserId, {
        formId: formDetail?.id || selectedFormId || "appraisal",
        answers,
        acceptedDeclaration: true,
      });
      const nextForms = await refreshForms();
      setFormRows(nextForms);
      setFormDetail((current) => current ? { ...current, readOnly: true, status: "Submitted" } : current);
      setDeclarationOpen(false);
      setMessage("Appraisal submitted.");
    } catch (submitError) {
      setError(submitError.message || "The appraisal could not be submitted.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestionControl = (question) => {
    const value = answers[question.id];
    const disabled = formDetail?.readOnly || busy;

    if (question.type === "rating") {
      const record = value && typeof value === "object" ? value : {};
      return (
        <div className="new-hrm-form-answer-grid">
          <div className="new-hrm-field new-hrm-appraisal-choice-field">
            <span>Rating</span>
            <div className="new-hrm-appraisal-rating-options" role="radiogroup" aria-label={question.title}>
              {[1, 2, 3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  type="button"
                  className={String(record.rating || "") === String(rating) ? "new-hrm-appraisal-rating-option new-hrm-appraisal-rating-option--selected" : "new-hrm-appraisal-rating-option"}
                  disabled={disabled}
                  aria-checked={String(record.rating || "") === String(rating)}
                  role="radio"
                  onClick={() => updateNestedAnswer(question.id, "rating", String(rating))}
                >
                  {rating}
                </button>
              ))}
            </div>
          </div>
          <label className="new-hrm-field">
            <span>Explanation</span>
            <textarea
              value={record.explanation || ""}
              disabled={disabled}
              placeholder={APPRAISAL_ANSWER_PLACEHOLDER}
              rows={5}
              onChange={(event) => updateNestedAnswer(question.id, "explanation", event.target.value)}
            />
          </label>
        </div>
      );
    }

    if (question.type === "choice" || question.type === "yesNo") {
      const record = value && typeof value === "object" ? value : {};
      const options = question.options || [];
      return (
        <div className="new-hrm-form-answer-grid">
          <div className="new-hrm-field new-hrm-appraisal-choice-field">
            <span>Answer</span>
            <div className="new-hrm-appraisal-choice-options" role="radiogroup" aria-label={question.title}>
              {options.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={record.choice === option ? "new-hrm-appraisal-choice-option new-hrm-appraisal-choice-option--selected" : "new-hrm-appraisal-choice-option"}
                  disabled={disabled}
                  aria-checked={record.choice === option}
                  role="radio"
                  onClick={() => updateNestedAnswer(question.id, "choice", option)}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
          <label className="new-hrm-field">
            <span>Explanation</span>
            <textarea
              value={record.explanation || ""}
              disabled={disabled}
              placeholder={APPRAISAL_ANSWER_PLACEHOLDER}
              rows={5}
              onChange={(event) => updateNestedAnswer(question.id, "explanation", event.target.value)}
            />
          </label>
        </div>
      );
    }

    return (
      <label className="new-hrm-field">
        <span>Response</span>
        <textarea
          value={typeof value === "string" ? value : ""}
          disabled={disabled}
          placeholder={APPRAISAL_ANSWER_PLACEHOLDER}
          rows={6}
          onChange={(event) => updateAnswer(question.id, event.target.value)}
        />
      </label>
    );
  };

  return (
    <section className="new-hrm-screen new-hrm-forms-screen new-hrm-loader-host">
      <ComponentLoader active={loading || detailLoading || busy} label={savingDraft ? "Saving draft" : submitting ? "Submitting appraisal" : loadingLabel} />
      <ScreenHeader
        eyebrow="Employee forms"
        title="My Forms"
        description="Complete active HRM forms assigned to you."
      />

      <div className="new-hrm-screen-metrics new-hrm-screen-metrics--compact">
        <ScreenMetric label="Draft" value={openCount} detail="Needs final submit" />
        <ScreenMetric label="Submitted" value={submittedCount} detail="Saved in Salesforce" />
        <ScreenMetric label="Active form" value={formRows.length ? formRows[0].title : "--"} detail="Current assignment" />
        <ScreenMetric label="Window" value="Closed" detail="No new submissions" />
      </div>

      <section className="new-hrm-panel new-hrm-forms-list-panel">
        <header className="new-hrm-panel__header new-hrm-panel__header--compact">
          <div>
            <h2>Available Forms</h2>
            <p>Save drafts as you go. Submit once every required answer is complete.</p>
          </div>
        </header>

        {formRows.length ? (
          <div className="new-hrm-forms-list">
            {formRows.map((form) => (
              <button
                key={form.id}
                type="button"
                className={`new-hrm-form-card ${selectedFormId === form.id ? "new-hrm-form-card--active" : ""}`}
                onClick={() => openForm(form.id)}
              >
                <span className="new-hrm-form-card__icon"><FiClipboard aria-hidden="true" /></span>
                <span>
                  <strong>{form.title}</strong>
                  <em>{form.description || "Candid performance appraisal response."}</em>
                </span>
                <span className={`new-hrm-form-status new-hrm-form-status--${String(form.status || "open").toLowerCase()}`}>
                  {form.requiresAction ? <FiAlertCircle aria-hidden="true" /> : <FiCheckCircle aria-hidden="true" />}
                  {form.status}
                </span>
                <small><FiClock aria-hidden="true" /> {form.dueText}</small>
              </button>
            ))}
          </div>
        ) : (
          <div className="new-hrm-empty-state">No available form right now.</div>
        )}
      </section>

      {formDetail && (
        <section className="new-hrm-panel new-hrm-appraisal-panel">
          <header className="new-hrm-panel__header">
            <div>
              <h2>{formDetail.title}</h2>
              <p>{formDetail.expiryNote}</p>
            </div>
            <span className={`new-hrm-form-status new-hrm-form-status--${String(formDetail.status || "open").toLowerCase()}`}>
              {formDetail.status}
            </span>
          </header>

          <div className="new-hrm-appraisal-info">
            <FiAlertCircle aria-hidden="true" />
            <p>{formDetail.info}</p>
          </div>

          <div className="new-hrm-appraisal-workspace">
            <aside className="new-hrm-appraisal-rail" aria-label="Form sections">
              <div className="new-hrm-appraisal-required-count">
                <strong>{completedRequiredCount}/{requiredCount}</strong>
                required complete
              </div>
              <nav>
                {stepNames.map((step, index) => {
                  const stepQuestions = (formDetail?.questions || []).filter((question) => (question.step || "Form") === step);
                  const stepRequired = stepQuestions.filter((question) => question.required);
                  const stepComplete = stepRequired.filter((question) => isAnswerFilled(question, answers[question.id])).length;
                  return (
                    <button
                      key={step}
                      type="button"
                      className={index === activeStepIndex ? "new-hrm-appraisal-rail-step new-hrm-appraisal-rail-step--active" : "new-hrm-appraisal-rail-step"}
                      onClick={() => setActiveStepIndex(index)}
                      disabled={busy}
                    >
                      <span>{index + 1}</span>
                      <strong>{step}</strong>
                      <em>{stepComplete}/{stepRequired.length || stepQuestions.length}</em>
                    </button>
                  );
                })}
              </nav>
            </aside>

            <div className="new-hrm-appraisal-main">
              <div className="new-hrm-appraisal-section-header">
                <div>
                  <span>Section {activeStepIndex + 1} of {stepNames.length}</span>
                  <h3>{activeStep}</h3>
                </div>
                <strong>{activeStepCompletedCount}/{activeStepRequiredCount || activeQuestions.length} complete</strong>
              </div>

              <div className="new-hrm-appraisal-questions">
                {activeQuestions.map((question, index) => (
                  <article key={question.id} className="new-hrm-appraisal-question">
                    <header>
                      <span>{index + 1}</span>
                      <div>
                        <h3>{question.title}</h3>
                        {question.helpText && <p>{question.helpText}</p>}
                      </div>
                      {question.required && <em>Required</em>}
                    </header>
                    {renderQuestionControl(question)}
                  </article>
                ))}
              </div>
            </div>
          </div>

          {error && <div className="new-hrm-form-message new-hrm-form-message--error" role="alert">{error}</div>}
          {message && <div className="new-hrm-form-message new-hrm-form-message--success" role="status">{message}</div>}

          <footer className="new-hrm-appraisal-actions">
            <span className="new-hrm-appraisal-actions__status">{missingRequiredCount ? `${missingRequiredCount} required left` : "Ready to save"}</span>
            <div className="new-hrm-appraisal-actions__buttons">
              <button
                type="button"
                className="new-hrm-secondary-button"
                disabled={activeStepIndex === 0 || busy}
                onClick={() => setActiveStepIndex((current) => Math.max(current - 1, 0))}
              >
                <FiChevronLeft aria-hidden="true" />
                Back
              </button>
              <button
                type="button"
                className="new-hrm-secondary-button"
                disabled={busy || formDetail.readOnly}
                onClick={() => saveDraft()}
              >
                <FiSave aria-hidden="true" />
                Save
              </button>
              {activeStepIndex < stepNames.length - 1 ? (
                <button
                  type="button"
                  className="new-hrm-primary-button"
                  disabled={busy || formDetail.readOnly}
                  onClick={() => saveDraft({ advance: true })}
                >
                  Save and Next
                  <FiChevronRight aria-hidden="true" />
                </button>
              ) : null}
              <button
                type="button"
                className="new-hrm-primary-button"
                disabled={busy || formDetail.readOnly || missingRequiredCount > 0}
                onClick={handleSubmit}
              >
                <FiSend aria-hidden="true" />
                Submit
              </button>
            </div>
          </footer>
        </section>
      )}

      {declarationOpen && (
        <div className="new-hrm-modal-backdrop">
          <section
            className="new-hrm-modal new-hrm-appraisal-declaration-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-hrm-appraisal-declaration-title"
          >
            <header className="new-hrm-modal__header">
              <div>
                <h2 id="new-hrm-appraisal-declaration-title">Appraisal Submission Declaration</h2>
                <p>Confirm this before final submission. Draft saving does not require this declaration.</p>
              </div>
              <button
                type="button"
                className="new-hrm-icon-button"
                onClick={() => setDeclarationOpen(false)}
                aria-label="Close declaration"
                disabled={submitting}
              >
                <FiX aria-hidden="true" />
              </button>
            </header>
            <div className="new-hrm-appraisal-declaration-modal__body">
              <p>
                I understand that this appraisal form will be used to understand my contribution, ownership, growth, and alignment with Antrazal's expectations.
              </p>
              <p>
                I confirm that every answer submitted here is written by me, in my own words, without using AI tools or copied material. My responses are based on facts, real examples, and my honest self-assessment.
              </p>
              <p>
                I understand that if my answers are found to be generic, copied, misleading, exaggerated, or AI-generated, the company may consider the submission unreliable during the appraisal review.
              </p>
              <label className="new-hrm-appraisal-declaration-modal__accept">
                <input
                  type="checkbox"
                  checked={declarationAccepted}
                  onChange={(event) => setDeclarationAccepted(event.target.checked)}
                  disabled={submitting}
                />
                <span>I accept and submit this form as my honest and original self-review.</span>
              </label>
            </div>
            <footer className="new-hrm-appraisal-declaration-modal__actions">
              <button
                type="button"
                className="new-hrm-secondary-button"
                onClick={() => setDeclarationOpen(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="new-hrm-primary-button"
                onClick={confirmSubmit}
                disabled={submitting || !declarationAccepted}
              >
                <FiSend aria-hidden="true" />
                Accept and Submit
              </button>
            </footer>
          </section>
        </div>
      )}
    </section>
  );
};

export default MyFormsScreen;
export { normalizeForms, normalizeFormDetail, isAnswerFilled };
